import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { Info, Loader2, Plus, UserPlus, X } from 'lucide-react';
import { listRoles } from '../../../services/api';
import { getAgencyClinics } from '../../../services/clinicsApi';
import type { AgencyClinicResponse } from '../../../services/clinicsApi';
import { withAgencyClinicDisplayNames } from '../../../utils/agencyClinicDisplayName';
import type {
  AgencyResponse,
  RoleResponse,
} from '../../../services/api/types.gen';
import type { UserType } from '../../../services/api/models/UserType';
import { USER_TYPE_OPTIONS, userTypeLabel } from '../../../constants/userTypes';
import { EMAIL_REGEX } from '../../../utils/emailUtils';
import { formatUsPhoneEntry, isCompleteUsPhone } from '../../../utils/intake';
import { filterUserVisibleRoles } from '../../../utils/rbacRoles';

/** A RogerDoc account to create for one invitee, collected alongside the invite. */
export interface InviteRogerDocAccountDraft {
  email: string;
  first_name: string;
  last_name: string;
  /** Human label for the title, which is what RogerDoc stores and displays. */
  clinician_title: string;
  /** The same title as an enum value, for the invitee's RogerEMR membership. */
  user_type: UserType | null;
  username?: string;
}

export interface InviteUserProfileDraft {
  email: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  user_type?: UserType | null;
}

export interface InviteUserToAgencyDialogProps {
  open: boolean;
  agencyId: string | null;
  /** When provided, an agency picker is shown and the user may switch agencies. */
  agencies?: AgencyResponse[];
  /**
   * Offer a RogerDoc account alongside each invite. RogerDoc derives the target
   * agency from the caller's context, so this stays off for cross-agency invites.
   */
  allowRogerDocAccounts?: boolean;
  onClose: () => void;
  onInvite: (params: {
    emails: string[];
    agency_id: string;
    role_ids: string[];
    profiles: InviteUserProfileDraft[];
    /** null = every clinic the inviter can access (the default); a list = only those. */
    clinic_ids: string[] | null;
    roger_doc_accounts: InviteRogerDocAccountDraft[];
  }) => Promise<void>;
}

/** Per-invitee RogerDoc form state, keyed by email in `rogerDocDrafts`. */
interface RogerDocDraftState {
  enabled: boolean;
  firstName: string;
  lastName: string;
  userType: UserType | null;
  username: string;
}

const EMPTY_ROGER_DOC_DRAFT: RogerDocDraftState = {
  enabled: false,
  firstName: '',
  lastName: '',
  userType: null,
  username: '',
};

/** Sentinel for the "not set" option, since Radix Select cannot hold an empty value. */
const NO_TITLE_VALUE = '__none__';

const InviteUserToAgencyDialog: React.FC<InviteUserToAgencyDialogProps> = ({
  open,
  agencyId,
  agencies,
  allowRogerDocAccounts = false,
  onClose,
  onInvite,
}) => {
  const showAgencyPicker = !!agencies;

  const [emailInput, setEmailInput] = useState('');
  const [emailInputError, setEmailInputError] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<Record<string, string>>({});
  const [rogerDocDrafts, setRogerDocDrafts] = useState<
    Record<string, RogerDocDraftState>
  >({});
  const [selectedAgencyId, setSelectedAgencyId] = useState<string>('');
  const [availableRoles, setAvailableRoles] = useState<RoleResponse[]>([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [agencyClinics, setAgencyClinics] = useState<AgencyClinicResponse[]>(
    []
  );
  // Default to every clinic, which the backend caps at the inviter's own
  // access — so an inviter who doesn't think about clinics neither locks the
  // invitee out nor hands them more reach than the inviter has.
  const [allClinicsSelected, setAllClinicsSelected] = useState(true);
  const [selectedClinicIds, setSelectedClinicIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setEmailInput('');
      setEmailInputError('');
      setEmails([]);
      setPhoneNumbers({});
      setRogerDocDrafts({});
      setSelectedRoleIds([]);
      setAvailableRoles([]);
      setIsLoadingRoles(false);
      setAgencyClinics([]);
      setAllClinicsSelected(true);
      setSelectedClinicIds([]);
      setIsSubmitting(false);
      return;
    }

    if (agencyId) setSelectedAgencyId(agencyId);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!selectedAgencyId) return;

    let isStale = false;
    setAllClinicsSelected(true);
    setSelectedClinicIds([]);
    getAgencyClinics(selectedAgencyId, 0, 1000)
      .then(clinics => {
        if (isStale) return;
        // Names resolved before the sort so an unnamed clinic sorts by the
        // label it is about to render under, not ahead of everything.
        setAgencyClinics(
          withAgencyClinicDisplayNames(clinics).sort((a, b) =>
            a.name.localeCompare(b.name)
          )
        );
      })
      .catch(error => {
        console.error('Failed to load agency clinics:', error);
        if (isStale) return;
        // With no list to pick from, "all clinics" stays selected — the
        // backend resolves that to the inviter's own clinics server-side, so
        // the invite still works and still cannot over-grant.
        setAgencyClinics([]);
      });
    return () => {
      isStale = true;
    };
  }, [open, selectedAgencyId]);

  useEffect(() => {
    if (!open) return;
    if (!selectedAgencyId) return;

    let isStale = false;
    setIsLoadingRoles(true);
    setSelectedRoleIds([]);
    listRoles({
      query: { agency_id: selectedAgencyId },
    })
      .then(res => {
        if (isStale) return;
        const roles = res.data ?? [];
        setAvailableRoles(
          filterUserVisibleRoles(
            roles.filter(role => role.scope !== 'clinic')
          ).sort(
            (a, b) =>
              Number(a.is_internal ?? false) - Number(b.is_internal ?? false)
          )
        );
      })
      .catch(error => {
        console.error('Failed to load agency roles:', error);
        if (isStale) return;
        setAvailableRoles([]);
      })
      .finally(() => {
        if (isStale) return;
        setIsLoadingRoles(false);
      });

    return () => {
      isStale = true;
    };
  }, [open, selectedAgencyId]);

  const addEmail = useCallback(() => {
    const trimmed = emailInput.trim().toLowerCase();
    if (!trimmed) return;

    if (!EMAIL_REGEX.test(trimmed)) {
      setEmailInputError('Please enter a valid email address');
      return;
    }
    if (emails.includes(trimmed)) {
      setEmailInputError('This email has already been added');
      return;
    }

    setEmails(prev => [...prev, trimmed]);
    setEmailInput('');
    setEmailInputError('');
    inputRef.current?.focus();
  }, [emailInput, emails]);

  const removeEmail = useCallback((email: string) => {
    setEmails(prev => prev.filter(e => e !== email));
    setPhoneNumbers(prev => {
      if (!(email in prev)) return prev;
      const next = { ...prev };
      delete next[email];
      return next;
    });
    // Drop the RogerDoc form state so a re-added email starts clean.
    setRogerDocDrafts(prev => {
      if (!(email in prev)) return prev;
      const next = { ...prev };
      delete next[email];
      return next;
    });
  }, []);

  const toggleRogerDocAccount = useCallback((email: string) => {
    setRogerDocDrafts(prev => {
      const current = prev[email] ?? EMPTY_ROGER_DOC_DRAFT;
      return { ...prev, [email]: { ...current, enabled: !current.enabled } };
    });
  }, []);

  const updateRogerDocDraft = useCallback(
    (email: string, patch: Partial<RogerDocDraftState>) => {
      setRogerDocDrafts(prev => ({
        ...prev,
        [email]: { ...(prev[email] ?? EMPTY_ROGER_DOC_DRAFT), ...patch },
      }));
    },
    []
  );

  const updatePhoneNumber = useCallback(
    (email: string, phoneNumber: string) => {
      // Format on the way in so a valid entry is always US-shaped. An
      // over-long paste is kept whole rather than trimmed to ten digits, so it
      // fails validation instead of quietly becoming a different number.
      setPhoneNumbers(prev => ({
        ...prev,
        [email]: formatUsPhoneEntry(phoneNumber),
      }));
    },
    []
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        addEmail();
      }
    },
    [addEmail]
  );

  const toggleRole = useCallback((roleId: string) => {
    setSelectedRoleIds(prev =>
      prev.includes(roleId)
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  }, []);

  const handleSubmit = async () => {
    const trimmed = emailInput.trim().toLowerCase();
    let finalEmails = emails;
    if (trimmed) {
      if (!EMAIL_REGEX.test(trimmed)) {
        setEmailInputError(
          'Please enter a valid email address or click the + button to add it'
        );
        return;
      }
      if (!emails.includes(trimmed)) {
        finalEmails = [...emails, trimmed];
        setEmails(finalEmails);
        setEmailInput('');
        setEmailInputError('');
      }
    }

    if (finalEmails.length === 0) {
      setEmailInputError('Please add at least one email address');
      return;
    }
    if (!selectedAgencyId) return;

    // An email typed but never added has no draft, so it is invited without a
    // RogerDoc account. Drafts for removed emails are dropped on removal.
    const enabledDrafts = allowRogerDocAccounts
      ? finalEmails
          .map(email => ({ email, draft: rogerDocDrafts[email] }))
          .filter(
            (entry): entry is { email: string; draft: RogerDocDraftState } =>
              !!entry.draft?.enabled
          )
      : [];

    // Defense in depth: the Send button is disabled while an opted-in row is
    // missing a name, so this is only reachable if that gate ever diverges.
    const hasIncompleteName = enabledDrafts.some(
      ({ draft }) => !draft.firstName.trim() || !draft.lastName.trim()
    );
    if (hasIncompleteName) return;

    // Same gate as the Send button: a half-typed number must not be sent.
    if (finalEmails.some(email => !isCompleteUsPhone(phoneNumbers[email]))) {
      return;
    }

    setIsSubmitting(true);
    try {
      const enabledDraftsByEmail = new Map(
        enabledDrafts.map(({ email, draft }) => [email, draft])
      );
      const profiles: InviteUserProfileDraft[] = [];
      for (const email of finalEmails) {
        const draft = enabledDraftsByEmail.get(email);
        const phoneNumber = (phoneNumbers[email] ?? '').trim();
        if (!draft && !phoneNumber) {
          continue;
        }
        profiles.push({
          email,
          first_name: draft?.firstName.trim() || undefined,
          last_name: draft?.lastName.trim() || undefined,
          phone_number: phoneNumber || undefined,
          user_type: draft ? draft.userType : undefined,
        });
      }

      await onInvite({
        emails: finalEmails,
        agency_id: selectedAgencyId,
        role_ids: selectedRoleIds,
        profiles,
        clinic_ids: allClinicsSelected ? null : selectedClinicIds,
        roger_doc_accounts: enabledDrafts.map(({ email, draft }) => ({
          email,
          first_name: draft.firstName.trim(),
          last_name: draft.lastName.trim(),
          clinician_title: userTypeLabel(draft.userType),
          user_type: draft.userType,
          // Omit when blank so the backend applies the house username format.
          username: draft.username.trim() || undefined,
        })),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * A row that opted into a RogerDoc account needs both names before anything can
   * be sent: RogerDoc builds the account from them, and the create endpoint is not
   * idempotent, so there is no safe retry once the invite has gone out.
   */
  const hasIncompleteRogerDocName =
    allowRogerDocAccounts &&
    emails.some(email => {
      const draft = rogerDocDrafts[email];
      return (
        !!draft?.enabled && (!draft.firstName.trim() || !draft.lastName.trim())
      );
    });

  /** Phone is optional, so only a part-typed number blocks sending. */
  const hasIncompletePhone = emails.some(
    email => !isCompleteUsPhone(phoneNumbers[email])
  );

  const canSubmit =
    !isSubmitting &&
    !isLoadingRoles &&
    selectedAgencyId &&
    (emails.length > 0 || emailInput.trim().length > 0) &&
    !hasIncompleteRogerDocName &&
    !hasIncompletePhone;

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className='sm:max-w-[540px] flex flex-col max-h-[90vh]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <UserPlus className='h-5 w-5' />
            Invite People
          </DialogTitle>
          <DialogDescription>
            {showAgencyPicker
              ? 'Add one or more email addresses, select an agency and roles, then send invitations.'
              : 'Add one or more email addresses, then select their roles.'}
          </DialogDescription>
        </DialogHeader>

        <div className='flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 shrink-0'>
          <Info className='h-4 w-4 shrink-0 text-blue-600' />
          <p className='text-sm text-blue-900'>
            Only new users will receive an email invitation.
          </p>
        </div>

        <div className='space-y-5 py-2 overflow-y-auto min-h-0 flex-1'>
          {/* Agency picker (super-user only) */}
          {showAgencyPicker && (
            <div className='space-y-1.5'>
              <Label>Agency</Label>
              <Select
                value={selectedAgencyId}
                onValueChange={value => setSelectedAgencyId(value)}
                disabled={isSubmitting}
              >
                <SelectTrigger selectSize='default'>
                  <SelectValue placeholder='Select an agency' />
                </SelectTrigger>
                <SelectContent>
                  {agencies!.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.written_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Roles */}
          <div className='space-y-2'>
            <Label>Roles</Label>
            <div className='space-y-2 max-h-56 overflow-y-auto border rounded-md p-3'>
              {availableRoles.map(role => (
                <div key={role.id} className='flex items-start gap-2'>
                  <Checkbox
                    id={`role-${role.id}`}
                    checked={selectedRoleIds.includes(role.id)}
                    onCheckedChange={() => toggleRole(role.id)}
                    disabled={isSubmitting}
                  />
                  <div className='flex flex-col'>
                    <Label
                      htmlFor={`role-${role.id}`}
                      className='font-medium cursor-pointer'
                    >
                      {role.name}
                    </Label>
                    {role.description && (
                      <span className='text-xs text-muted-foreground'>
                        {role.description}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {isLoadingRoles && (
              <p className='text-xs text-muted-foreground'>Loading roles…</p>
            )}
          </div>

          {/* Clinic access — the clinics the invitee will be able to see. */}
          <div className='space-y-2'>
            <Label>Clinic access</Label>
            <div className='space-y-2 max-h-56 overflow-y-auto border rounded-md p-3'>
              <div className='flex items-start gap-2'>
                <Checkbox
                  id='invite-clinics-all'
                  checked={allClinicsSelected}
                  onCheckedChange={checked => {
                    const all = checked === true;
                    setAllClinicsSelected(all);
                    if (all) setSelectedClinicIds([]);
                  }}
                  disabled={isSubmitting}
                />
                <Label
                  htmlFor='invite-clinics-all'
                  className='font-medium cursor-pointer'
                >
                  All clinics
                </Label>
              </div>
              {!allClinicsSelected &&
                agencyClinics.map(clinic => (
                  <div key={clinic.id} className='flex items-start gap-2 pl-6'>
                    <Checkbox
                      id={`invite-clinic-${clinic.id}`}
                      checked={selectedClinicIds.includes(clinic.id)}
                      onCheckedChange={() =>
                        setSelectedClinicIds(prev =>
                          prev.includes(clinic.id)
                            ? prev.filter(id => id !== clinic.id)
                            : [...prev, clinic.id]
                        )
                      }
                      disabled={isSubmitting}
                    />
                    <Label
                      htmlFor={`invite-clinic-${clinic.id}`}
                      className='cursor-pointer'
                    >
                      {clinic.name}
                    </Label>
                  </div>
                ))}
            </div>
            {!allClinicsSelected && selectedClinicIds.length === 0 && (
              <p className='text-xs text-muted-foreground'>
                No clinics selected — the invitee will not see any clinic's
                patients until access is granted in User Manager.
              </p>
            )}
          </div>

          {/* Email input */}
          <div className='space-y-1.5'>
            <Label htmlFor='invite-email-agency'>Email addresses</Label>
            <div className='flex gap-2'>
              <Input
                ref={inputRef}
                id='invite-email-agency'
                type='email'
                placeholder='name@example.com'
                value={emailInput}
                onChange={e => {
                  setEmailInput(e.target.value);
                  if (emailInputError) setEmailInputError('');
                }}
                onKeyDown={handleKeyDown}
                disabled={isSubmitting}
                autoFocus
                className='flex-1'
              />
              <Button
                type='button'
                variant='outline'
                size='default'
                onClick={addEmail}
                disabled={isSubmitting || !emailInput.trim()}
                aria-label='Add email'
                className='flex-shrink-0 gap-1.5 px-3'
              >
                <Plus className='h-4 w-4' />
                Add
              </Button>
            </div>
            {emailInputError && (
              <p className='text-sm text-destructive'>{emailInputError}</p>
            )}
            <p className='text-xs text-muted-foreground'>
              Press Enter or click Add after each email address.
            </p>
          </div>

          {/* Email list */}
          {emails.length > 0 && (
            <div className='space-y-1.5'>
              <div className='flex items-center justify-between'>
                <Label className='text-sm'>
                  Ready to invite
                  <span className='ml-1.5 inline-flex items-center justify-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary'>
                    {emails.length}
                  </span>
                </Label>
                <button
                  type='button'
                  onClick={() => {
                    setEmails([]);
                    setPhoneNumbers({});
                    setRogerDocDrafts({});
                  }}
                  className='text-xs text-muted-foreground hover:text-destructive transition-colors'
                  disabled={isSubmitting}
                >
                  Clear all
                </button>
              </div>
              {/* The dialog body scrolls, so rows expand instead of nesting a
                  second scroll area around the RogerDoc fields. */}
              <div className='rounded-md border bg-muted/30 p-2 space-y-2'>
                {emails.map(email => {
                  const draft = rogerDocDrafts[email] ?? EMPTY_ROGER_DOC_DRAFT;
                  // Shown as soon as the row is incomplete rather than on submit,
                  // because the Send button is disabled until it is filled in and
                  // would otherwise give no reason.
                  const missingName =
                    draft.enabled &&
                    (!draft.firstName.trim() || !draft.lastName.trim());
                  return (
                    <div key={email} className='rounded border bg-background'>
                      <div className='flex items-center justify-between gap-2 px-2 py-1 text-sm'>
                        <span className='truncate'>{email}</span>
                        <button
                          type='button'
                          onClick={() => removeEmail(email)}
                          disabled={isSubmitting}
                          className='flex-shrink-0 text-muted-foreground hover:text-destructive transition-colors'
                          aria-label={`Remove ${email}`}
                        >
                          <X className='h-3.5 w-3.5' />
                        </button>
                      </div>

                      <div className='border-t px-2 py-2'>
                        <Label
                          htmlFor={`invite-phone-${email}`}
                          className='mb-1 block text-xs font-medium'
                        >
                          Phone number
                        </Label>
                        <Input
                          id={`invite-phone-${email}`}
                          aria-label={`Phone number for ${email}`}
                          inputSize='sm'
                          type='tel'
                          value={phoneNumbers[email] ?? ''}
                          onChange={e =>
                            updatePhoneNumber(email, e.target.value)
                          }
                          disabled={isSubmitting}
                          placeholder='555-555-5555'
                        />
                        {!isCompleteUsPhone(phoneNumbers[email]) && (
                          <p
                            className='mt-1 text-xs text-destructive'
                            role='alert'
                          >
                            Enter a 10-digit US phone number
                          </p>
                        )}
                      </div>

                      {allowRogerDocAccounts && (
                        <div className='space-y-2 border-t px-2 py-2'>
                          <div className='flex items-center gap-2'>
                            <Checkbox
                              id={`rogerdoc-${email}`}
                              checked={draft.enabled}
                              onCheckedChange={() =>
                                toggleRogerDocAccount(email)
                              }
                              disabled={isSubmitting}
                            />
                            <Label
                              htmlFor={`rogerdoc-${email}`}
                              className='cursor-pointer text-xs font-medium'
                            >
                              Also create a Roger App account
                            </Label>
                          </div>

                          {draft.enabled && (
                            <div className='space-y-2'>
                              <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
                                <div>
                                  <Label
                                    htmlFor={`rogerdoc-first-${email}`}
                                    className='mb-1 block text-xs font-medium'
                                  >
                                    First name
                                  </Label>
                                  <Input
                                    id={`rogerdoc-first-${email}`}
                                    inputSize='sm'
                                    value={draft.firstName}
                                    onChange={e =>
                                      updateRogerDocDraft(email, {
                                        firstName: e.target.value,
                                      })
                                    }
                                    disabled={isSubmitting}
                                    placeholder='First name'
                                  />
                                </div>
                                <div>
                                  <Label
                                    htmlFor={`rogerdoc-last-${email}`}
                                    className='mb-1 block text-xs font-medium'
                                  >
                                    Last name
                                  </Label>
                                  <Input
                                    id={`rogerdoc-last-${email}`}
                                    inputSize='sm'
                                    value={draft.lastName}
                                    onChange={e =>
                                      updateRogerDocDraft(email, {
                                        lastName: e.target.value,
                                      })
                                    }
                                    disabled={isSubmitting}
                                    placeholder='Last name'
                                  />
                                </div>
                              </div>
                              <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
                                <div>
                                  <Label
                                    htmlFor={`rogerdoc-title-${email}`}
                                    className='mb-1 block text-xs font-medium'
                                  >
                                    Clinical title (optional)
                                  </Label>
                                  <Select
                                    value={draft.userType ?? NO_TITLE_VALUE}
                                    onValueChange={next =>
                                      updateRogerDocDraft(email, {
                                        userType:
                                          next === NO_TITLE_VALUE
                                            ? null
                                            : (next as UserType),
                                      })
                                    }
                                    disabled={isSubmitting}
                                  >
                                    <SelectTrigger
                                      id={`rogerdoc-title-${email}`}
                                      aria-label={`Clinical title for ${email}`}
                                      selectSize='sm'
                                    >
                                      <SelectValue placeholder='Not set' />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value={NO_TITLE_VALUE}>
                                        Not set
                                      </SelectItem>
                                      {USER_TYPE_OPTIONS.map(option => (
                                        <SelectItem
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label
                                    htmlFor={`rogerdoc-username-${email}`}
                                    className='mb-1 block text-xs font-medium'
                                  >
                                    Username (optional)
                                  </Label>
                                  <Input
                                    id={`rogerdoc-username-${email}`}
                                    inputSize='sm'
                                    value={draft.username}
                                    onChange={e =>
                                      updateRogerDocDraft(email, {
                                        username: e.target.value,
                                      })
                                    }
                                    disabled={isSubmitting}
                                    placeholder='Auto-generated from name if blank'
                                  />
                                </div>
                              </div>
                              {missingName && (
                                <p className='text-xs text-muted-foreground'>
                                  First and last name are required.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting && <Loader2 className='h-4 w-4 animate-spin mr-2' />}
            {emails.length > 1
              ? `Send ${emails.length} Invitations`
              : 'Send Invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteUserToAgencyDialog;
